/**
 * Utility functions for quarter calculations
 */

export interface QuarterInfo {
  current: string;
  previous: string;
}

/**
 * Determines the current and previous quarters based on the current date.
 * Applies a 10-day buffer by subtracting 10 days from current date,
 * then returns quarter-1 and quarter-2 to account for data availability lag.
 * 
 * Algorithm:
 * 1. Subtract 10 days from current date
 * 2. Determine which quarter that adjusted date falls in
 * 3. Return (quarter - 1) and (quarter - 2)
 * 
 * Example: If today is Jan 20, 2025:
 * - Adjusted date = Jan 10, 2025 → Q1-25
 * - Return Q4-24 (Q1-1) and Q3-24 (Q1-2)
 * 
 * @param date - The date to calculate quarters for (defaults to current date)
 * @returns Object containing current and previous quarter strings (e.g., "Q4-24", "Q3-24")
 */
export const getCurrentAndPreviousQuarters = (date: Date = new Date()): QuarterInfo => {
  // Subtract 10 days from the current date to handle the buffer
  const adjustedDate = new Date(date);
  adjustedDate.setDate(adjustedDate.getDate() - 10);
  
  const month = adjustedDate.getMonth(); // 0-11
  const year = adjustedDate.getFullYear();
  
  // Calculate current quarter (1-4) directly from month
  const currentQuarter = Math.floor(month / 3) + 1;
  
  // Calculate quarter-1 and quarter-2
  const q1 = currentQuarter - 1;
  const q2 = currentQuarter - 2;
  
  // Handle year wrapping
  const currentYear = q1 <= 0 ? year - 1 : year;
  const previousYear = q2 <= 0 ? year - 1 : year;
  
  // Wrap quarters to 1-4 range
  const currentQuarterNum = q1 <= 0 ? q1 + 4 : q1;
  const previousQuarterNum = q2 <= 0 ? q2 + 4 : q2;
  
  return {
    current: `Q${currentQuarterNum}-${currentYear.toString().slice(-2)}`,
    previous: `Q${previousQuarterNum}-${previousYear.toString().slice(-2)}`,
  };
};
